import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../../utils/authUtils';
import { useLanguage } from '../../utils/LanguageContext';

const GUEST_POPUP_KEY = 'rumahkira_guest_popup_seen';

const GuestSignupPopup = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const content = {
    en: {
      title: 'Get More from RumahKira',
      subtitle: 'Create a free account to unlock all features',
      benefits: [
        'Save unlimited projects',
        'Affordable Weekly Pass (RM3) or Monthly Pass (RM7)',
        'Unlimited calculations & estimates',
        'Export detailed PDF reports',
      ],
      signUp: 'Sign Up Free',
      maybeLater: 'Maybe Later',
    },
    my: {
      title: 'Dapatkan Lebih dari RumahKira',
      subtitle: 'Cipta akaun percuma untuk buka semua ciri',
      benefits: [
        'Simpan projek tanpa had',
        'Pas Mingguan (RM3) atau Pas Bulanan (RM7) yang berpatutan',
        'Pengiraan & anggaran tanpa had',
        'Eksport laporan PDF terperinci',
      ],
      signUp: 'Daftar Percuma',
      maybeLater: 'Mungkin Nanti',
    },
  };

  const t = content[language] || content.en;

  useEffect(() => {
    if (user) return;
    if (sessionStorage.getItem(GUEST_POPUP_KEY)) return;

    const timer = setTimeout(() => {
      setOpen(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, [user]);

  const handleClose = () => {
    setOpen(false);
    sessionStorage.setItem(GUEST_POPUP_KEY, '1');
  };

  const handleSignUp = () => {
    handleClose();
    navigate('/auth');
  };

  if (user) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, overflow: 'hidden' },
      }}
    >
      {/* Gradient Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
          py: 3,
          px: 3,
          position: 'relative',
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PersonAddIcon sx={{ fontSize: 28, color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} color="white">
              {t.title}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              {t.subtitle}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Benefits */}
      <DialogContent sx={{ p: 3 }}>
        <List disablePadding>
          {t.benefits.map((benefit, i) => (
            <ListItem key={i} disablePadding sx={{ mb: 1 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckCircleIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={benefit}
                primaryTypographyProps={{
                  variant: 'body2',
                  fontWeight: i === 1 ? 600 : 400,
                  color: i === 1 ? 'primary.main' : 'text.primary',
                }}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ p: 3, pt: 0, flexDirection: 'column', gap: 1 }}>
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleSignUp}
          sx={{ py: 1.5 }}
        >
          {t.signUp}
        </Button>
        <Button
          fullWidth
          variant="text"
          onClick={handleClose}
          sx={{ color: 'text.disabled', fontSize: '0.875rem' }}
        >
          {t.maybeLater}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GuestSignupPopup;
